import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendIcon from "@material-ui/icons/Send";
import LinkIcon from "@material-ui/icons/Link";
import DescriptionIcon from "@material-ui/icons/Description";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import ConfirmationModal from "../../components/ConfirmationModal";
import ProposalModal from "../../components/ProposalModal";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { displayClientName } from "../../utils/clientName";
import copyToClipboard from "../../utils/copyToClipboard";

export const PROPOSAL_STATUS = {
  draft: { label: "Rascunho", tone: "neutral" },
  sent: { label: "Enviada", tone: "info" },
  viewed: { label: "Visualizada", tone: "warning" },
  accepted: { label: "Aceita", tone: "success" },
  rejected: { label: "Recusada", tone: "danger" },
  expired: { label: "Vencida", tone: "warning" },
  canceled: { label: "Cancelada", tone: "neutral" },
};

const useStyles = makeStyles((theme) => ({
  search: { minWidth: 260 },
  filter: { minWidth: 160 },
  muted: { color: theme.palette.text.hint },
  secondary: { color: theme.palette.text.secondary },
  titleCell: { display: "flex", flexDirection: "column", gap: 1 },
}));

const money = (v, c = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: c });

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : null;

/**
 * O texto anterior prometia conta a receber no aceite. Deixou de ser verdade
 * quando o pedido entrou no meio do caminho: manter os dois caminhos cobraria
 * o cliente duas vezes pela mesma venda. Descrever um fluxo que não existe
 * mais faz a pessoa procurar a cobrança em Contas a Receber, não encontrar e
 * concluir que o aceite falhou.
 */
const SUBTITULO =
  "Orçamentos enviados ao cliente com aceite registrado. Aceitar move o " +
  "negócio para ganho e abre um pedido em rascunho — a cobrança é emitida ao " +
  "faturar esse pedido, em Pedidos.";

const Proposals = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/proposals", {
        params: {
          searchParam: searchParam || undefined,
          status: status || undefined,
          pageSize: 100,
        },
      });
      setRows(data.proposals || []);
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchParam, status]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 350);
    return () => clearTimeout(t);
  }, [fetchRows]);

  // O cliente pode abrir ou responder a qualquer momento; sem socket o
  // comercial só descobriria recarregando.
  useSocketSubscription("proposal", fetchRows);

  const handleSend = async (row) => {
    try {
      const { data } = await api.post(`/proposals/${row.id}/send`, {});
      await copyToClipboard(data.message);
      toast.success("Proposta publicada. Mensagem copiada para a área de transferência.");
      fetchRows();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCopyLink = async (row) => {
    try {
      const { data } = await api.get(`/proposals/${row.id}`);
      await copyToClipboard(data.publicUrl);
      toast.success("Link copiado.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/proposals/${deleting.id}`);
      toast.success("Proposta removida");
      fetchRows();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const dash = <span className={classes.muted}>—</span>;

  const columns = [
    {
      key: "number",
      label: "Número",
      render: (r) => (
        <Typography variant="subtitle2" style={{ fontVariantNumeric: "tabular-nums" }}>
          {r.number}
        </Typography>
      ),
    },
    {
      key: "title",
      label: "Proposta",
      render: (r) => (
        <div className={classes.titleCell}>
          <Typography variant="subtitle2">{r.title}</Typography>
          <Typography variant="caption" className={classes.secondary}>
            {displayClientName(r.client, "Sem cliente")}
          </Typography>
        </div>
      ),
    },
    {
      key: "totalAmount",
      label: "Valor",
      type: "numeric",
      render: (r) => money(r.totalAmount, r.currency),
    },
    {
      key: "validUntil",
      label: "Validade",
      render: (r) => fmtDate(r.validUntil) || dash,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => {
        const meta = PROPOSAL_STATUS[r.status] || { label: r.status, tone: "neutral" };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (r) => (
        <>
          {!["accepted", "rejected"].includes(r.status) && (
            <Tooltip title={r.status === "draft" ? "Publicar e copiar mensagem" : "Copiar mensagem novamente"}>
              <IconButton size="small" onClick={() => handleSend(r)}>
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {r.status !== "draft" && (
            <Tooltip title="Copiar link público">
              <IconButton size="small" onClick={() => handleCopyLink(r)}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Editar">
            <span>
              <IconButton
                size="small"
                disabled={["accepted", "rejected"].includes(r.status)}
                onClick={() => {
                  setEditingId(r.id);
                  setModalOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Excluir">
            <span>
              <IconButton
                size="small"
                disabled={r.status === "accepted"}
                onClick={() => setDeleting(r)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      <ProposalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        proposalId={editingId}
        onSaved={fetchRows}
      />
      <ConfirmationModal
        title={deleting ? `Excluir a proposta ${deleting.number}?` : ""}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      >
        O link público deixa de funcionar imediatamente.
      </ConfirmationModal>

      <PageHeader
        title="Propostas"
        count={count}
        subtitle={SUBTITULO}
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingId(null);
              setModalOpen(true);
            }}
          >
            Nova proposta
          </Button>
        }
        filters={
          <>
            <TextField
              className={classes.search}
              placeholder="Buscar por número ou título"
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              className={classes.filter}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(PROPOSAL_STATUS).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon={<DescriptionIcon />}
        emptyTitle={searchParam || status ? "Nenhuma proposta encontrada" : "Nenhuma proposta ainda"}
        emptyDescription={
          searchParam || status
            ? "Ajuste a busca ou o filtro de status."
            : "Gere uma proposta a partir de um negócio do pipeline: os itens vêm prontos e o cliente aceita pelo link."
        }
        emptyAction={
          !searchParam && !status ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingId(null);
                setModalOpen(true);
              }}
            >
              Nova proposta
            </Button>
          ) : null
        }
      />
    </MainContainer>
  );
};

export default Proposals;
